import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowRight, Shield, Trash2, Plus, X, Save } from 'lucide-react';
import { rbacService, authService } from '../services/api';

function AdminRoles() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', slug: '', description: '' });

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        rbacService.getRoles(),
        rbacService.getPermissions(),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permissionsRes.data);
    } catch (err) {
      setError('خطا در دریافت اطلاعات نقش‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await rbacService.deleteRole(id);
      setRoles(roles.filter(r => r.id !== id));
    } catch (err) {
      setError('خطا در حذف نقش');
    }
  };

  const toggleRole = async (role) => {
    if (selectedRole?.id === role.id) {
      setSelectedRole(null);
      setSelectedPermissionIds([]);
      return;
    }
    try {
      const res = await rbacService.getRolePermissions(role.id);
      const permissionIds = res.data
        .map(key => {
          for (const group of permissions) {
            const found = group.permissions.find(p => p.key === key);
            if (found) return found.id;
          }
          return null;
        })
        .filter(id => id !== null);
      setSelectedRole(role);
      setSelectedPermissionIds(permissionIds);
    } catch (err) {
      setError('خطا در دریافت دسترسی‌های نقش');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissionIds(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleModuleToggle = (modulePermissions, allSelected) => {
    const moduleIds = modulePermissions.map(p => p.id);
    if (allSelected) {
      setSelectedPermissionIds(prev => prev.filter(id => !moduleIds.includes(id)));
    } else {
      setSelectedPermissionIds(prev => Array.from(new Set([...prev, ...moduleIds])));
    }
  };

  const handleSavePermissions = async () => {
    try {
      await rbacService.setRolePermissions(selectedRole.id, selectedPermissionIds);
      setSelectedRole(null);
      setSelectedPermissionIds([]);
      fetchData();
    } catch (err) {
      setError('خطا در ذخیره دسترسی‌ها');
    }
  };

  const handleCancel = () => {
    setSelectedRole(null);
    setSelectedPermissionIds([]);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await rbacService.createRole({
        name: newRole.name,
        slug: newRole.slug,
        description: newRole.description,
      });
      setRoles([...roles, res.data]);
      setShowCreate(false);
      setNewRole({ name: '', slug: '', description: '' });
    } catch (err) {
      setError('خطا در ایجاد نقش');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت نقش‌ها</h1>
              <p className="text-xl text-blue-100">مشاهده، ایجاد و تنظیم دسترسی‌ها</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/admin/articles')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition"
              >
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
              >
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        <div className="mb-6">
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            {showCreate ? <X size={18} /> : <Plus size={18} />}
            {showCreate ? 'انصراف' : 'نقش جدید'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="نام نقش"
                value={newRole.name}
                onChange={e => setNewRole({ ...newRole, name: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="نامک (slug)"
                value={newRole.slug}
                onChange={e => setNewRole({ ...newRole, slug: e.target.value })}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="توضیحات"
                value={newRole.description}
                onChange={e => setNewRole({ ...newRole, description: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              type="submit"
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              ایجاد
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>
        ) : (
          <div className="space-y-4">
            {roles.map(role => (
              <div key={role.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-6 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-700">
                      <Shield size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{role.name}</h3>
                      <p className="text-gray-600 text-sm">{role.slug} {role.isSystem && <span className="text-blue-600 font-semibold mr-2">(سیستمی)</span>}</p>
                      {role.description && <p className="text-gray-500 text-xs mt-1">{role.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRole(role)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm ${
                        selectedRole?.id === role.id
                          ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {selectedRole?.id === role.id ? <X size={16} /> : <Shield size={16} />}
                      {selectedRole?.id === role.id ? 'بستن' : 'ویرایش دسترسی‌ها'}
                    </button>
                    {!role.isSystem && (
                      <button
                        onClick={() => handleDelete(role.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                      >
                        <Trash2 size={16} />
                        حذف
                      </button>
                    )}
                  </div>
                </div>

                {selectedRole?.id === role.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-gray-900">ویرایش دسترسی‌ها</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSavePermissions}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          <Save size={16} />
                          ذخیره
                        </button>
                        <button
                          onClick={handleCancel}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                        >
                          انصراف
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {permissions.map(group => {
                        const allSelected = group.permissions.every(p => selectedPermissionIds.includes(p.id));
                        return (
                          <div key={group.module} className="bg-white rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-semibold text-gray-800">{group.module}</h5>
                              <button
                                type="button"
                                onClick={() => handleModuleToggle(group.permissions, allSelected)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                {allSelected ? 'حذف همه' : 'انتخاب همه'}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {group.permissions.map(p => (
                                <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissionIds.includes(p.id)}
                                    onChange={() => handlePermissionToggle(p.id)}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">{p.action}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRoles;
